import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export const ContextSwitcher: React.FC = () => {
  const { user, switchContext, isSuperAdmin } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const shouldShow = (user?.available_clients?.length ?? 0) > 1 || isSuperAdmin();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  if (!shouldShow) return null;

  const handleSwitch = async (clientId: string) => {
    if (clientId === user?.active_client?.id) return;
    setIsOpen(false);
    await switchContext(clientId);
    // Rester sur la page courante et recharger pour repropager le nouveau
    // contexte tenant à tous les data fetchs de la page (au lieu de
    // forcer un retour à la racine, qui redirige vers /extraction/documents).
    window.location.reload();
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors border border-gray-200"
      >
        <span className="font-medium">{user?.active_client?.name ?? 'Sélectionner un contexte'}</span>
        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[200px] py-1 z-50">
          {user?.available_clients?.map((client) => {
            const isCurrent = client.id === user?.active_client?.id;
            return (
              <button
                key={client.id}
                onClick={() => handleSwitch(client.id)}
                className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${
                  isCurrent
                    ? 'bg-[#d4e5f5] text-primary font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {isCurrent && <Check size={14} className="flex-shrink-0" />}
                {!isCurrent && <span className="w-[14px]" />}
                <span>{client.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
